import React from 'react';
import { RefreshCw, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';

// App version for error reports
const APP_VERSION = '1.1.9';

// Supported language codes for validation
const VALID_LANGUAGES = [
  'en-US', 'en-GB', 'af', 'ar', 'da', 'de', 'el', 'es', 'fr', 'hi', 'id', 'it',
  'ja', 'ko', 'ms', 'nl', 'pl', 'pt-BR', 'pt-PT', 'ro', 'ru', 'sk', 'sv', 'tr',
  'vi', 'zh-CN', 'zh-TW'
];

// Safe storage helper
const safeStorage = {
  getItem: (key) => {
    try { return localStorage.getItem(key); } catch { return null; }
  },
  setItem: (key, value) => {
    try { localStorage.setItem(key, value); return true; } catch { return false; }
  },
  removeItem: (key) => {
    try { localStorage.removeItem(key); return true; } catch { return false; }
  },
  clear: () => {
    try { localStorage.clear(); return true; } catch { return false; }
  }
};

// Send error to backend (best effort, no throw)
const reportErrorToBackend = async (errorData) => {
  try {
    const payload = {
      message: String(errorData.message || 'Unknown error').slice(0, 1000),
      stack: String(errorData.stack || '').slice(0, 2000),
      url: window.location.href,
      version: APP_VERSION,
      ua: navigator.userAgent,
      lang: navigator.language,
      extra: {
        features: window.__features || {},
        timestamp: new Date().toISOString()
      }
    };
    
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal
    });
    
    clearTimeout(timeout);
  } catch (e) {
    // Silently fail - don't crash on error reporting
    console.debug('[ErrorBoundary] Failed to report error:', e);
  }
};

// Track reported errors to avoid duplicates
const reportedErrors = new Set();
const reportErrorOnce = (error) => {
  const key = `${error.message}-${error.stack?.slice(0, 100)}`;
  if (reportedErrors.has(key)) return;
  reportedErrors.add(key);
  reportErrorToBackend(error);
};

// Register global error handlers (outside class to run once)
if (typeof window !== 'undefined') {
  window.onerror = (message, source, lineno, colno, error) => {
    reportErrorOnce({
      message: `${message} at ${source}:${lineno}:${colno}`,
      stack: error?.stack || ''
    });
  };
  
  window.onunhandledrejection = (event) => {
    const error = event.reason;
    reportErrorOnce({
      message: `Unhandled Promise: ${error?.message || String(error)}`,
      stack: error?.stack || ''
    });
  };
}

/**
 * AppErrorBoundary - Global error boundary with diagnostic info
 * Critical for Huawei/GMS compatibility where Google services may fail
 */
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      showDetails: false
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[AppErrorBoundary] Caught error:', error);
    console.error('[AppErrorBoundary] Error info:', errorInfo);
    
    this.setState({ errorInfo });
    
    // Report to backend
    reportErrorOnce({
      message: error.message,
      stack: `${error.stack}\n\nComponent Stack:${errorInfo?.componentStack || 'N/A'}`
    });
  }

  handleRetry = () => {
    // Clear error state and re-render
    this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false });
  };

  handleReload = async () => {
    // Preserve only language and theme preferences
    const savedLang = safeStorage.getItem('parachpay_language');
    const savedTheme = safeStorage.getItem('parachpay_theme');
    
    // Validate saved values
    const validLang = savedLang && VALID_LANGUAGES.includes(savedLang) ? savedLang : null;
    const validTheme = savedTheme && ['light', 'dark'].includes(savedTheme) ? savedTheme : null;
    
    // Clear all localStorage
    safeStorage.clear();
    
    // Restore valid preferences
    if (validLang) safeStorage.setItem('parachpay_language', validLang);
    if (validTheme) safeStorage.setItem('parachpay_theme', validTheme);
    
    // Unregister service workers
    if ('serviceWorker' in navigator) {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const reg of registrations) {
          await reg.unregister();
        }
      } catch (e) {
        console.debug('[ErrorBoundary] SW unregister failed:', e);
      }
    }
    
    // Clear caches
    if ('caches' in window) {
      try {
        const keys = await caches.keys();
        for (const key of keys) {
          await caches.delete(key);
        }
      } catch (e) {
        console.debug('[ErrorBoundary] Cache clear failed:', e);
      }
    }
    
    // Full page reload (replace to prevent back-loop)
    window.location.replace('/');
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  getErrorDetails = () => {
    const { error, errorInfo } = this.state;
    const features = window.__features || {};
    
    // Get first ~6 lines of stack
    const stackLines = (error?.stack || '')
      .split('\n')
      .slice(0, 6)
      .join('\n');
    
    return {
      message: error?.message || 'Unknown error',
      stack: stackLines,
      version: APP_VERSION,
      userAgent: navigator.userAgent,
      language: navigator.language,
      url: window.location.href,
      features: {
        IntlLocale: features.hasIntlLocale,
        IntlListFormat: features.hasIntlListFormat,
        IntlRelativeTimeFormat: features.hasIntlRelativeTimeFormat,
        IntlPluralRules: features.hasIntlPluralRules,
        ResizeObserver: features.hasResizeObserver,
        IntersectionObserver: features.hasIntersectionObserver,
        ObjectHasOwn: features.hasObjectHasOwn,
        ArrayAt: features.hasArrayAt,
        ReplaceAll: features.hasReplaceAll
      },
      componentStack: errorInfo?.componentStack?.slice(0, 500) || 'N/A'
    };
  };

  render() {
    if (this.state.hasError) {
      const details = this.getErrorDetails();
      
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-emerald-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center">
            {/* Logo */}
            <div className="mb-8">
              <div className="w-20 h-20 mx-auto bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg shadow-emerald-500/25">
                <span className="text-white font-bold text-2xl">P</span>
              </div>
              <h1 className="mt-4 text-2xl font-bold text-white">ParachPay</h1>
            </div>
            
            {/* Error Message */}
            <div className="bg-slate-800/50 rounded-2xl p-6 backdrop-blur-sm border border-slate-700">
              <div className="w-12 h-12 mx-auto bg-amber-500/20 rounded-full flex items-center justify-center mb-4">
                <AlertTriangle className="w-6 h-6 text-amber-500" />
              </div>
              
              <h2 className="text-xl font-semibold text-white mb-2">
                Something went wrong
              </h2>
              <p className="text-slate-400 mb-6">
                We encountered an unexpected error. Please try again or reload the app.
              </p>
              
              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={this.handleRetry}
                  className="inline-flex items-center justify-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </button>
                <button
                  onClick={this.handleReload}
                  className="inline-flex items-center justify-center px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  Reload App
                </button>
              </div>
              
              {/* Collapsible Details */}
              <div className="mt-6 border-t border-slate-700 pt-4">
                <button
                  onClick={this.toggleDetails}
                  className="text-sm text-slate-500 hover:text-slate-400 flex items-center justify-center gap-1 mx-auto"
                >
                  {this.state.showDetails ? (
                    <>
                      <ChevronUp className="w-4 h-4" />
                      Hide Details
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4" />
                      Show Details
                    </>
                  )}
                </button>
                
                {this.state.showDetails && (
                  <div className="mt-4 text-left bg-slate-900/50 rounded-lg p-3 text-xs font-mono overflow-auto max-h-64">
                    <div className="text-slate-400 mb-2">
                      <span className="text-amber-400">Version:</span> {details.version}
                    </div>
                    <div className="text-slate-400 mb-2">
                      <span className="text-amber-400">Error:</span> {details.message}
                    </div>
                    <div className="text-slate-400 mb-2 whitespace-pre-wrap">
                      <span className="text-amber-400">Stack:</span>
                      {'\n'}{details.stack || 'N/A'}
                    </div>
                    <div className="text-slate-400 mb-2 break-all">
                      <span className="text-amber-400">UA:</span> {details.userAgent}
                    </div>
                    <div className="text-slate-400 mb-2">
                      <span className="text-amber-400">Lang:</span> {details.language}
                    </div>
                    <div className="text-slate-400">
                      <span className="text-amber-400">Intl Features:</span>
                      {'\n'}Locale: {String(details.features.IntlLocale)}
                      {'\n'}ListFormat: {String(details.features.IntlListFormat)}
                      {'\n'}RelativeTime: {String(details.features.IntlRelativeTimeFormat)}
                      {'\n'}PluralRules: {String(details.features.IntlPluralRules)}
                      {'\n'}ResizeObserver: {String(details.features.ResizeObserver)}
                      {'\n'}IntersectionObserver: {String(details.features.IntersectionObserver)}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Support Link */}
            <p className="mt-6 text-sm text-slate-500">
              If the problem persists,{' '}
              <a 
                href="mailto:support@parachpay.com" 
                className="text-emerald-400 hover:underline"
              >
                contact support
              </a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
